import { db } from '../firebase';
import { getArancelPrice, getConvenioDiscount, aplicarDescuentoPct } from '../utils/finance';
import { toLocalDateStr, isScheduleActiveOnDate } from '../utils/dateUtils';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  Timestamp,
  onSnapshot,
  runTransaction,
} from 'firebase/firestore';

/**
 * Genera un código de reserva único de 8 caracteres alfanuméricos
 */
export const generateBookingCode = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 8; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

/**
 * Obtiene los slots disponibles para una fecha y profesional específicos
 */
export const getAvailableSlots = async (date, professionalId, branchId = null, specialtyId = null) => {
  // Usar zona horaria local en lugar de UTC para evitar problemas en iOS
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const dateStr = `${year}-${month}-${day}`;
  // Fix zona horaria: usar la fecha local, no UTC
  const dayOfWeek = String(date.getDay());

  console.log('🔍 getAvailableSlots:', { dateStr, dayOfWeek, professionalId, branchId, specialtyId });

  // 1. Obtener la agenda del profesional buscando por professionalId en lugar de ID del documento
  const schedSnap = await getDocs(
    query(collection(db, 'professionalSchedules'), where('professionalId', '==', professionalId))
  );

  console.log('📄 Agendas encontradas para profesional:', schedSnap.size);

  if (schedSnap.empty) return [];

  const toIsoDate = (v) => {
    if (!v) return '';
    if (typeof v === 'string') return v.split('T')[0];
    return toLocalDateStr(v);
  };

  // Un profesional puede tener varias agendas (distinta sucursal y/o especialidad).
  // Se debe usar únicamente la agenda que corresponde a la sucursal y especialidad
  // solicitadas, y que esté vigente para la fecha consultada.
  let candidates = schedSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

  if (branchId) candidates = candidates.filter((s) => s.branchId === branchId);
  if (specialtyId) candidates = candidates.filter((s) => s.specialtyId === specialtyId);

  candidates = candidates.filter((s) => {
    const startStr = toIsoDate(s.startDate);
    const endStr = toIsoDate(s.endDate);
    if (startStr && dateStr < startStr) return false;
    if (endStr && dateStr > endStr) return false;
    // Validar recurrencia (continuous, biweekly, monthly)
    return isScheduleActiveOnDate({ ...s, startDate: startStr, endDate: endStr }, dateStr);
  });

  console.log('🏢 Agendas que coinciden con sucursal/especialidad/vigencia:', candidates.length);

  if (candidates.length === 0) return [];

  // Fusionar slots de todas las agendas que coinciden (no solo candidates[0])
  // Si hay múltiples agendas (distinta consulta) para la misma sucursal+especialidad,
  // se combinan los slots sumando capacidades por hora.
  const slotMap = {};
  const scheduleIds = [];
  const capacityIds = [];
  for (const sched of candidates) {
    const daySchedule = sched.schedule?.[dayOfWeek];
    if (!daySchedule?.active || !daySchedule.slots?.length) continue;
    scheduleIds.push(sched.id);
    if (sched.capacityId) capacityIds.push(sched.capacityId);
    for (const slot of daySchedule.slots) {
      if (slotMap[slot.time]) {
        slotMap[slot.time].capacity += slot.capacity || 1;
      } else {
        slotMap[slot.time] = { time: slot.time, label: slot.label || slot.time, capacity: slot.capacity || 1, visibleOnline: slot.visibleOnline !== false };
      }
    }
  }

  let allSlots = Object.values(slotMap);
  if (allSlots.length === 0) return [];

  // 2. Filtrar slots por visibleOnline
  allSlots = allSlots.filter((slot) => slot.visibleOnline !== false);

  // Filtrar slots cuya hora ya pasó (solo para el día de hoy)
  const todayStr = (() => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
  })();
  if (dateStr === todayStr) {
    const now = new Date();
    const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    allSlots = allSlots.filter((slot) => slot.time > currentTime);
  }

  // 3. Verificar que las capacidades estén asociadas a la sucursal (si se proporciona branchId)
  if (branchId && capacityIds.length > 0) {
    for (const capId of capacityIds) {
      const capacitySnap = await getDoc(doc(db, 'capacities', capId));
      if (capacitySnap.exists()) {
        const capacityData = capacitySnap.data();
        if (capacityData.branchIds && !capacityData.branchIds.includes(branchId)) {
          // Esta capacidad no está asociada a esta sucursal; remover sus slots
          // (en la práctica, si todas las agendas son de la misma sucursal, esto no debería pasar)
        }
      }
    }
  }

  // 4. Verificar bloqueos para esa fecha
  // B2: Filtrar bloqueos por scheduleId (con fallback a clínica+especialidad para datos legacy)
  const blocksSnap = await getDocs(
    query(
      collection(db, 'scheduleBlocks'),
      where('professionalId', '==', professionalId),
      where('date', '==', dateStr)
    )
  );

  const scheduleIdSet = new Set(scheduleIds);
  for (const blockDoc of blocksSnap.docs) {
    const block = blockDoc.data();
    // Si el bloqueo tiene scheduleId, solo aplica si coincide con una de las agendas vigentes
    if (block.scheduleId) {
      if (!scheduleIdSet.has(block.scheduleId)) continue;
    }
    if (block.blockType === 'full_day') {
      allSlots = [];
      break;
    }
    // A7: Fin de bloqueo exclusivo (no bloquear el slot que empieza exactamente en endTime)
    if (block.startTime && block.endTime) {
      allSlots = allSlots.filter(
        (s) => !(s.time >= block.startTime && s.time < block.endTime)
      );
    }
  }

  // 5. Obtener reservas existentes para ese profesional y fecha
  const bookedSnap = await getDocs(
    query(
      collection(db, 'onlineBookings'),
      where('date', '==', dateStr),
      where('professionalId', '==', professionalId),
      where('status', 'in', ['confirmed', 'pending', 'pending_payment', 'en_sala'])
    )
  );

  const now = new Date();
  const bookedTimes = {};
  bookedSnap.docs.forEach((d) => {
    const data = d.data();
    if (branchId && data.branchId !== branchId) return;
    // Para pending_payment, ignorar si ya expiró
    if (data.status === 'pending_payment') {
      const expiresAt = data.expiresAt && data.expiresAt.toDate ? data.expiresAt.toDate() : null;
      if (expiresAt && expiresAt <= now) return;
    }
    const t = data.time;
    bookedTimes[t] = (bookedTimes[t] || 0) + 1;
  });

  return allSlots.map((slot) => ({
    time: slot.time,
    label: slot.label,
    capacity: slot.capacity || 1,
    booked: bookedTimes[slot.time] || 0,
    available: (bookedTimes[slot.time] || 0) < (slot.capacity || 1),
  }));
};

/**
 * Obtiene el precio de consulta desde la colección aranceles,
 * buscando primero por specialtyId + branchId, luego solo por specialtyId.
 * Si se entrega un convenio, aplica el descuento asociado.
 */
export const getConsultationPrice = async (specialtyId, branchId = null, convenio = null) => {
  try {
    const q = query(
      collection(db, 'aranceles'),
      where('specialtyId', '==', specialtyId),
      where('activo', '==', true)
    );
    const snap = await getDocs(q);
    for (const docItem of snap.docs) {
      const arancel = docItem.data();
      const price = getArancelPrice(arancel, branchId);
      if (price > 0) {
        if (convenio) {
          const discountPct = getConvenioDiscount(convenio, arancel.tipoPrestacionName, docItem.id);
          return aplicarDescuentoPct(price, discountPct);
        }
        return price;
      }
    }
    return 0;
  } catch (e) {
    console.error('Error obteniendo precio de consulta:', e);
    return 0;
  }
};

/**
 * Devuelve el documento completo del arancel de consulta (con id) para una
 * especialidad/sucursal, sin aplicar descuentos. Útil para generar presupuestos
 * donde el descuento del convenio se registra desglosado por ítem.
 */
export const getConsultationArancel = async (specialtyId, branchId = null) => {
  try {
    const q = query(
      collection(db, 'aranceles'),
      where('specialtyId', '==', specialtyId),
      where('activo', '==', true)
    );
    const snap = await getDocs(q);
    for (const docItem of snap.docs) {
      const arancel = { id: docItem.id, ...docItem.data() };
      if (getArancelPrice(arancel, branchId) > 0) return arancel;
    }
    return null;
  } catch (e) {
    console.error('Error obteniendo arancel de consulta:', e);
    return null;
  }
};

/**
 * Actualiza el estado de una reserva buscándola por bookingCode
 */
export const updateBookingStatus = async (bookingCode, newStatus, details = {}) => {
  const q = query(collection(db, 'onlineBookings'), where('bookingCode', '==', bookingCode));
  const snap = await getDocs(q);
  if (snap.empty) throw new Error('Reserva no encontrada');
  await updateDoc(snap.docs[0].ref, {
    status: newStatus,
    ...details,
    updatedAt: Timestamp.now(),
  });
};

/**
 * Crea una reserva directa (sin pago — para consultas gratuitas) con verificación previa de disponibilidad
 * Usa runTransaction para evitar race conditions (dos usuarios simultáneos excediendo el cupo).
 */
export const createBooking = async (bookingData) => {
  const { date, time, professionalId, branchId } = bookingData;
  const bookingCode = generateBookingCode();

  // Fix zona horaria: parsear 'YYYY-MM-DD' como fecha local (mediodía) en vez de UTC midnight
  const dayOfWeek = String(new Date(`${date}T12:00:00`).getDay());

  // Obtener capacidad desde la agenda del profesional (fuera de la transacción para no bloquear)
  const schedSnap = await getDocs(
    query(
      collection(db, 'professionalSchedules'),
      where('professionalId', '==', professionalId)
    )
  );
  let slotCapacity = 1;
  let scheduleId = '';
  let capacityId = '';
  const scheduleDoc = schedSnap.docs.find((d) => {
    const data = d.data();
    if (!data.schedule?.[dayOfWeek]?.active) return false;
    if (branchId && data.branchId !== branchId) return false;
    // Validar recurrencia (continuous, biweekly, monthly)
    return isScheduleActiveOnDate(data, date);
  }) || schedSnap.docs[0];
  if (scheduleDoc) {
    const data = scheduleDoc.data();
    scheduleId = scheduleDoc.id;
    capacityId = data.capacityId || '';
    const slots = data.schedule?.[dayOfWeek]?.slots || [];
    const slot = slots.find((s) => s.time === time);
    if (slot) slotCapacity = slot.capacity || 1;
  }

  // Transacción: verificar cupo y crear la reserva atómicamente
  const newRef = doc(collection(db, 'onlineBookings'));
  const sobrecupoFlag = !!bookingData.sobrecupo;

  await runTransaction(db, async (tx) => {
    const existingSnap = await getDocs(
      query(
        collection(db, 'onlineBookings'),
        where('date', '==', date),
        where('time', '==', time),
        where('professionalId', '==', professionalId),
        where('status', 'in', ['no_confirmado', 'confirmed', 'pending', 'pending_payment', 'en_sala'])
      )
    );

    let existingForBranch = 0;
    let sobrecuposForBranch = 0;
    const now = new Date();
    existingSnap.docs.forEach((d) => {
      const b = d.data();
      if (branchId && b.branchId !== branchId) return;
      if (b.status === 'pending_payment') {
        const expiresAt = b.expiresAt && b.expiresAt.toDate ? b.expiresAt.toDate() : null;
        if (expiresAt && expiresAt <= now) return;
      }
      existingForBranch++;
      if (b.sobrecupo) sobrecuposForBranch++;
    });

    if (!sobrecupoFlag && existingForBranch >= slotCapacity) {
      throw new Error('El horario seleccionado ya no está disponible. Por favor elige otro.');
    }

    if (sobrecupoFlag && sobrecuposForBranch >= 2) {
      throw new Error('Ya se han agotado los sobrecupos para esta agenda (máximo 2).');
    }

    const dataToSave = { ...bookingData };
    dataToSave.sobrecupo = sobrecupoFlag;
    tx.set(newRef, {
      ...dataToSave,
      bookingCode,
      scheduleId: scheduleId || '',
      capacityId: capacityId || '',
      confirmationStatus: 'pending',
      status: dataToSave.status || 'confirmed',
      createdAt: Timestamp.now(),
      source: dataToSave.source || 'online',
    });
  });

  // C2: Si las notificaciones están activadas, enviar confirmación por WhatsApp automáticamente
  // (no bloquea el retorno; si falla, la cita ya se creó)
  try {
    const notifSnap = await getDoc(doc(db, 'settings', 'notifications'));
    const notifConfig = notifSnap.exists() ? notifSnap.data() : null;
    if (notifConfig?.enabled && bookingData.ownerCelular) {
      const { httpsCallable } = await import('firebase/functions');
      const { functions } = await import('../firebase');
      const sendConfirmation = httpsCallable(functions, 'sendBookingConfirmation');
      sendConfirmation({ bookingCode }).catch((e) =>
        console.warn('No se pudo enviar confirmación automática:', e.message)
      );
    }
  } catch (e) {
    console.warn('Notificaciones: no se pudo verificar configuración:', e.message);
  }

  return bookingCode;
};

/**
 * Suscripción en tiempo real a los slots de una fecha y profesional
 */
export const subscribeToSlots = (date, professionalId, callback, branchId = null) => {
  const dateStr =
    typeof date === 'string'
      ? date
      : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  const q = query(
    collection(db, 'onlineBookings'),
    where('date', '==', dateStr),
    where('professionalId', '==', professionalId),
    where('status', 'in', ['confirmed', 'pending', 'pending_payment', 'en_sala'])
  );
  return onSnapshot(q, (snap) => {
    const now = new Date();
    const booked = {};
    snap.docs.forEach((d) => {
      const data = d.data();
      if (branchId && data.branchId !== branchId) return;
      if (data.status === 'pending_payment') {
        const expiresAt = data.expiresAt && data.expiresAt.toDate ? data.expiresAt.toDate() : null;
        if (expiresAt && expiresAt <= now) return;
      }
      const t = data.time;
      booked[t] = (booked[t] || 0) + 1;
    });
    callback(booked);
  });
};

/**
 * D1: Confirma una cita por parte del paciente (vía link email/WhatsApp)
 * Marca la cita con confirmationStatus = 'confirmed_by_patient'
 */
export const confirmBookingByPatient = async (bookingCode) => {
  const q = query(collection(db, 'onlineBookings'), where('bookingCode', '==', bookingCode));
  const snap = await getDocs(q);
  if (snap.empty) throw new Error('Reserva no encontrada');
  await updateDoc(snap.docs[0].ref, {
    confirmationStatus: 'confirmed_by_patient',
    confirmedByPatientAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  });
};

/**
 * D2: Notifica automáticamente al primer paciente en lista de espera cuando se cancela una cita
 * Marca la entrada de waitlist como notified y retorna los datos para enviar el mensaje
 */
export const notifyNextWaitlistEntry = async (professionalId, date, slotTime) => {
  const q = query(
    collection(db, 'waitlistEntries'),
    where('professionalId', '==', professionalId),
    where('date', '==', date),
    where('slotTime', '==', slotTime),
    where('status', '==', 'waiting')
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;

  const entries = snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => {
      const aTime = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
      const bTime = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
      return aTime - bTime;
    });

  const first = entries[0];
  await updateDoc(doc(db, 'waitlistEntries', first.id), {
    status: 'notified',
    notifiedAt: Timestamp.now(),
  });

  return {
    id: first.id,
    ownerName: first.ownerName || '',
    ownerCelular: first.ownerCelular || '',
    petName: first.petName || '',
    date: first.date,
    slotTime: first.slotTime,
  };
};
